import {useMemo} from "react"
import {useSelector} from "react-redux"
import type {StoreState} from "../store"
import {useGetSelfUserQuery} from "../user/userApi"
import type {IUserPermissionFlags, IUserSelf} from "../user/UserApiTypes"
import {normalizePermissionFlags} from "../user/userUtils"
import {
    APP_MODULES,
    getAppModule,
    getAppModuleForPath,
    isAccessRequirementSatisfied,
    SETTINGS_MODULE_PATHS
} from "./appRegistry"
import {PERMISSION_KEYS, PermissionKey} from "./permissions"


export {APP_MODULES, getAppModule, getAppModuleForPath, PERMISSION_KEYS}
export type {PermissionKey}

export interface AccessPolicy {
    username: string | null
    accountId: number | null
    user: IUserSelf | null
    permission: IUserPermissionFlags
    isLoading: boolean
    isReady: boolean
    isAdmin: boolean
    branchId?: string
    branchIds?: string[]
    isBranchRestricted: boolean
    canUseSystem: boolean
    canReadUsers: boolean
    canWriteUsers: boolean
    canReadBranches: boolean
    canWriteBranches: boolean
    canManageBranches: boolean
    canReadMenu: boolean
    canWriteMenu: boolean
    canReadCouriers: boolean
    canWriteCouriers: boolean
    canReadReviews: boolean
    canWriteReviews: boolean
    canReadOrders: boolean
    canWriteOrders: boolean
    canReadPromotions: boolean
    canWritePromotions: boolean
    canSelectBranches: boolean
    canEditBranchSettings: boolean
    canCreateCourier: boolean
    canEditCourierBranchScope: boolean
}

const decodeJwtPayload = (token?: string | null): Record<string, unknown> | null => {
    if (!token) return null
    const parts = token.split(".")
    if (parts.length < 2) return null

    try {
        const payload = parts[1]
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/")
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")
        const decoded = atob(padded)
        return JSON.parse(decoded) as Record<string, unknown>
    } catch {
        return null
    }
}

const extractNumericId = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) return value
    if (typeof value === "string" && /^\d+$/.test(value.trim())) return Number(value)
    return null
}

export const getAccountIdFromToken = (token?: string | null) => {
    const payload = decodeJwtPayload(token)
    if (!payload) return null

    const candidates = [
        payload.account_id,
        payload.accountId,
        payload.user_id,
        payload.userId,
        payload.id,
        payload.sub
    ]

    for (const candidate of candidates) {
        const accountId = extractNumericId(candidate)
        if (accountId !== null) return accountId
    }

    return null
}

const normalizeUsername = (username?: string | null) => username?.trim().toLowerCase() || null

const hasPermission = (permission: IUserPermissionFlags, key: PermissionKey) => Boolean(permission[key])

const getBranchIdsFromUser = (user?: IUserSelf | null) => {
    const source = user as (IUserSelf & {
        branch_id?: string
        branch_id_list?: string[]
        branch_list?: Array<{id?: string}>
    }) | null | undefined
    const branchIds = [
        source?.branch_id,
        ...(source?.branch_id_list || []),
        ...((source?.branch_list || []).map((branch) => branch.id).filter(Boolean) as string[])
    ].filter((branchId): branchId is string => Boolean(branchId))

    return Array.from(new Set(branchIds))
}

export const getAccessPolicy = (
    accountId: number | null,
    username?: string | null,
    user?: IUserSelf | null,
    isLoading = false
): AccessPolicy => {
    const permission = normalizePermissionFlags(user?.permission || user?.permission_list)
    const branchIds = getBranchIdsFromUser(user)
    const isBranchRestricted = Boolean(branchIds.length)
    const canUseSystem = hasPermission(permission, "use_system")
    const canReadBranches = hasPermission(permission, "read_branch")
    const canWriteBranches = hasPermission(permission, "write_branch")
    const canManageBranches = hasPermission(permission, "manage_branch")
    const canReadCouriers = hasPermission(permission, "read_courier")
    const canWriteCouriers = hasPermission(permission, "write_courier")
    const canWriteUsers = hasPermission(permission, "write_user")
    const isAdmin = PERMISSION_KEYS.every((key) => hasPermission(permission, key))

    return {
        username: normalizeUsername(user?.username || username),
        accountId: user?.id || accountId,
        user: user || null,
        permission,
        isLoading,
        isReady: !isLoading && Boolean(user),
        isAdmin,
        branchIds: isBranchRestricted ? branchIds : undefined,
        branchId: isBranchRestricted ? branchIds[0] : undefined,
        isBranchRestricted,
        canUseSystem,
        canReadUsers: hasPermission(permission, "read_user"),
        canWriteUsers,
        canReadBranches,
        canWriteBranches,
        canManageBranches,
        canReadMenu: hasPermission(permission, "read_menu"),
        canWriteMenu: hasPermission(permission, "write_menu"),
        canReadCouriers,
        canWriteCouriers,
        canReadReviews: hasPermission(permission, "read_review"),
        canWriteReviews: hasPermission(permission, "write_review"),
        canReadOrders: hasPermission(permission, "read_order"),
        canWriteOrders: hasPermission(permission, "write_order"),
        canReadPromotions: hasPermission(permission, "read_promotion"),
        canWritePromotions: hasPermission(permission, "write_promotion"),
        canSelectBranches: canReadBranches || canManageBranches,
        canEditBranchSettings: canWriteBranches || canManageBranches,
        canCreateCourier: canWriteCouriers,
        canEditCourierBranchScope: canWriteCouriers && (canReadBranches || canManageBranches),
    }
}

export const applyBranchScope = (branchIds: string[] | undefined, policy: AccessPolicy): string[] | undefined => {
    const requestedIds = branchIds && Array.from(new Set(branchIds.map(id => id.trim()).filter(Boolean)))
    if (!policy.isBranchRestricted) return requestedIds
    const allowedIds = policy.branchIds || []
    return requestedIds === undefined
        ? [...allowedIds]
        : requestedIds.filter(id => allowedIds.includes(id))
}

/** An empty scope must never be sent as an unfiltered API request. */
export const isBranchScopeEmpty = (branchIds: string[] | undefined) => branchIds?.length === 0

export const getScopedBranchIds = (
    policy: AccessPolicy,
    branchIds?: string[],
    selectedBranchId?: string
): string[] | undefined => {
    if (!policy.isReady) return []
    const requestedIds = selectedBranchId ? [selectedBranchId] : branchIds?.length ? branchIds : undefined
    return applyBranchScope(requestedIds, policy)
}

export const isBranchAllowed = (branchId: string | null | undefined, policy: AccessPolicy) => {
    if (!policy.isBranchRestricted) return true
    if (!branchId) return false
    return policy.branchIds?.includes(branchId) || false
}

export const getPathPermission = (path: string): PermissionKey | undefined => {
    const requirement = getAppModuleForPath(path)?.access
    return requirement?.allOf?.length === 1 && !requirement.anyOf?.length
        ? requirement.allOf[0]
        : undefined
}

export const isPathAllowed = (path: string, policy: AccessPolicy): boolean => {
    if (policy.isLoading) return false
    if (!policy.canUseSystem) return false
    if (path === "/settings") return SETTINGS_MODULE_PATHS.some((settingsPath) => isPathAllowed(settingsPath, policy))

    const module = getAppModuleForPath(path)
    if (!module) return hasPermission(policy.permission, "use_system")
    if (module.enabled === false) return false

    return isAccessRequirementSatisfied(module.access, policy.permission)
}

export const getFirstAllowedPath = (paths: string[], policy: AccessPolicy) =>
    paths.find((path) => isPathAllowed(path, policy))

export const getDefaultAuthenticatedPath = (policy: AccessPolicy) =>
    getFirstAllowedPath(["/", "/orders", "/delivery-center", "/reviews", "/menu", "/settings/couriers", "/settings/branches"], policy) || "/profile"

export const getFirstAllowedSettingsPath = (policy: AccessPolicy) =>
    getFirstAllowedPath(SETTINGS_MODULE_PATHS, policy)

export const useAccessPolicy = () => {
    const token = useSelector((state: StoreState) => state.auth.token)
    const username = useSelector((state: StoreState) => state.auth.username)
    const {data, isLoading, isFetching, isUninitialized} = useGetSelfUserQuery(
        undefined,
        {skip: !token}
    )

    return useMemo(() => {
        const accountId = getAccountIdFromToken(token)
        const isInitialUserLoading = Boolean(token) && !data?.data && (isLoading || isFetching || isUninitialized)
        return getAccessPolicy(accountId, username, data?.data, isInitialUserLoading)
    }, [data?.data, isFetching, isLoading, isUninitialized, token, username])
}
